<script setup>

import {storeToRefs} from "pinia";
import {onMounted, ref} from "vue";
import {useUserAdvocatePatientStore} from "@/stores/userAdvocatePatient.store";
import {useRoute} from "vue-router";
import {useGlobalStore} from "@/stores/global.store";

const route = useRoute();
const userAdvocatePatientStore = useUserAdvocatePatientStore();
const globalStore = useGlobalStore();

const {
    userExercises,
    userSessions,
    userSurveys,
    userInstructions,
    userFiles,
    userForms
} = storeToRefs(userAdvocatePatientStore);

const active = ref(0);

onMounted(async () => {
    globalStore.loading = true;
    await userAdvocatePatientStore.fetchExercises(route.params.id);
    await userAdvocatePatientStore.fetchSessions(route.params.id);
    await userAdvocatePatientStore.fetchForms(route.params.id);
    await userAdvocatePatientStore.fetchSurveys(route.params.id);
    await userAdvocatePatientStore.fetchInstructions(route.params.id);
    await userAdvocatePatientStore.fetchFiles(route.params.id);
    globalStore.loading = false;
});

</script>

<template>
    <van-space direction="vertical" fill>
        <van-tabs v-model:active="active">
            <van-tab :title="'Sessions ' + '(' + userSessions.length + ')'" name="session">
                <div v-if="userSessions.length === 0">
                    <van-empty image="search" description="Nothing found"/>
                </div>
                <div v-if="userSessions.length > 0">
                    <van-cell-group inset v-for="userSession in userSessions"
                                    :key="userSession.id">
                        <van-cell title="Name" :value="userSession.name"/>
                        <van-cell title="Status" :value="userSession.status"/>
                        <van-cell title="Counselors" :value="userSession.staff_user"/>
                        <van-cell title="Scheduled" :value="userSession.schedule_at_formatted"/>
                        <van-cell title="Created" :value="userSession.created_at_formatted"/>
                        <van-row>&nbsp;</van-row>
                    </van-cell-group>
                </div>
            </van-tab>
            <van-tab :title="'Exercise ' + '(' + userExercises.length + ')'" name="exercise">
                <div v-if="userExercises.length === 0">
                    <van-empty image="search" description="Nothing found"/>
                </div>
                <div v-if="userExercises.length > 0">
                    <van-cell-group inset v-for="userExercise in userExercises" :key="userExercise.id">
                        <van-cell title="Name" :value="userExercise.name"/>
                        <van-cell title="Completed" :value="userExercise.completed_at_formatted"/>
                        <van-cell title="Session" :value="userExercise.treatment_session"/>
                        <van-cell title="Created" :value="userExercise.created_at_formatted"/>
                        <van-row>&nbsp;</van-row>
                    </van-cell-group>
                </div>
            </van-tab>
            <van-tab :title="'Instruction ' + '(' + userInstructions.length + ')'" name="Instructions">
                <div v-if="userInstructions.length === 0">
                    <van-empty image="search" description="Nothing found"/>
                </div>
                <div v-if="userInstructions.length > 0">
                    <van-cell-group inset v-for="userInstruction in userInstructions" :key="userInstruction.id">
                        <van-cell title="Name" :value="userInstruction.name"/>
                        <van-cell title="Status" :value="userInstruction.status"/>
                        <van-cell title="Session" :value="userInstruction.treatment_session"/>
                        <van-cell title="Created" :value="userInstruction.created_at_formatted"/>
                        <van-row>&nbsp;</van-row>
                    </van-cell-group>
                </div>
            </van-tab>
            <van-tab :title="'Form ' + '(' + userForms.length + ')'" name="Forms">
                <div v-if="userForms.length === 0">
                    <van-empty image="search" description="Nothing found"/>
                </div>
                <div v-if="userForms.length > 0">
                    <van-cell-group inset v-for="userForm in userForms" :key="userForm.id">
                        <van-cell title="Name" :value="userForm.name"/>
                        <van-cell title="Status" :value="userForm.status"/>
                        <van-cell title="Session" :value="userForm.treatment_session"/>
                        <van-cell title="Created" :value="userForm.created_at_formatted"/>
                        <van-row>&nbsp;</van-row>
                    </van-cell-group>
                </div>
            </van-tab>
            <van-tab :title="'File ' + '(' + userFiles.length + ')'" name="Files">
                <div v-if="userFiles.length === 0">
                    <van-empty image="search" description="Nothing found"/>
                </div>
                <div v-if="userFiles.length > 0">
                    <van-cell-group inset v-for="userFile in userFiles" :key="userFile.id">
                        <van-cell title="Name" :value="userFile.name"/>
                        <van-cell title="Status" :value="userFile.status"/>
                        <van-cell title="Session" :value="userFile.treatment_session"/>
                        <van-cell title="Created" :value="userFile.created_at_formatted"/>
                        <van-row>&nbsp;</van-row>
                    </van-cell-group>
                </div>
            </van-tab>
            <van-tab :title="'Survey ' + '(' + userSurveys.length + ')'" name="Surveys">
                <div v-if="userSurveys.length === 0">
                    <van-empty image="search" description="Nothing found"/>
                </div>
                <div v-if="userSurveys.length > 0">
                    <van-cell-group inset v-for="userSurvey in userSurveys" :key="userSurvey.id">
                        <van-cell title="Name" :value="userSurvey.name"/>
                        <van-cell title="Status" :value="userSurvey.status"/>
                        <van-cell title="Session" :value="userSurvey.treatment_session"/>
                        <van-cell title="Created" :value="userSurvey.created_at_formatted"/>
                        <van-row>&nbsp;</van-row>
                    </van-cell-group>
                </div>
            </van-tab>
        </van-tabs>
    </van-space>
</template>

<style>

:root:root {
    --van-grid-item-text-font-size: var(--van-font-size-xs);
}

</style>
